import type { SlotState } from '../types'

export interface SlotPose {
  id: string
  position: { x: number; z: number }
}

/**
 * Слот, чья квадратная AABB (tileSize) покрывает точку игрока на XZ.
 * Правая/дальняя граница полуоткрыта — без двойного попадания на стыке.
 */
export function slotCoveringPosition(
  slots: SlotPose[],
  x: number,
  z: number,
  tileSize: number,
): string | null {
  const half = tileSize / 2
  for (const s of slots) {
    const dx = x - s.position.x
    const dz = z - s.position.z
    if (dx >= -half && dx < half && dz >= -half && dz < half) {
      return s.id
    }
  }
  return null
}

/**
 * Ближайший слот в радиусе (евклид до центра).
 * Не использовать для entrance scan — там virtual slot.
 */
export function nearestSlotInRange(
  slots: SlotPose[],
  x: number,
  z: number,
  maxDist: number,
): string | null {
  let best: string | null = null
  let bestD = maxDist * maxDist
  for (const s of slots) {
    const dx = x - s.position.x
    const dz = z - s.position.z
    const d = dx * dx + dz * dz
    if (d <= bestD) {
      bestD = d
      best = s.id
    }
  }
  return best
}

/** Первая линия поля: все слоты с минимальным z (entry-facing row). */
export function frontRowSlotIds(slots: SlotPose[]): string[] {
  if (slots.length === 0) return []
  let minZ = Infinity
  for (const s of slots) {
    if (s.position.z < minZ) minZ = s.position.z
  }
  const eps = 1e-6
  return slots.filter((s) => Math.abs(s.position.z - minZ) <= eps).map((s) => s.id)
}

/** Adjacent виртуального входа = число мин на всей первой линии. */
export function virtualEntranceMineCount(
  graph: { getSlot(id: string): { hasMine: boolean } | undefined },
  frontRowIds: string[],
): number {
  let count = 0
  for (const id of frontRowIds) {
    if (graph.getSlot(id)?.hasMine) count += 1
  }
  return count
}

/**
 * Зона сканера у входа: не на слоте, южнее первой линии,
 * в радиусе tile*1.35 от ближайшего front-row центра.
 */
export function inEntranceScanZone(
  slots: SlotPose[],
  x: number,
  z: number,
  tileSize: number,
): boolean {
  const front = frontRowSlotIds(slots)
  if (front.length === 0) return false

  const frontSlots = slots.filter((s) => front.includes(s.id))
  const fieldMinZ = Math.min(...frontSlots.map((s) => s.position.z))
  if (z >= fieldMinZ) return false

  const maxDist = tileSize * 1.35
  return nearestSlotInRange(frontSlots, x, z, maxDist) !== null
}

/** Мина под ногами без defused = взрыв; по обезвреженной ходить можно. */
export function shouldExplodeOnStep(slot: {
  hasMine: boolean
  state: SlotState
} | undefined): boolean {
  if (!slot) return false
  return slot.hasMine && slot.state !== 'defused'
}
